// src/features/tuition/components/finances/InvoiceForm.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import { fetchStudents } from '../../services/studentsService';

const InvoiceForm = ({ open, onClose, onSubmit, initialData = null }) => {
  const [students, setStudents] = useState([]);
  const [formData, setFormData] = useState({
    studentId: '',
    studentName: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(new Date().setDate(new Date().getDate() + 14)).toISOString().split('T')[0],
    items: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
    subtotal: 0,
    tax: 0,
    total: 0,
    status: 'unpaid',
    paidAmount: 0,
    notes: ''
  });
  
  useEffect(() => {
    // Fetch students for dropdown
    const loadStudents = async () => {
      try {
        const studentsData = await fetchStudents();
        setStudents(studentsData);
      } catch (error) {
        console.error("Error loading students:", error);
        
        // For demo purposes, add mock data
        setStudents([
          { id: '1', firstName: 'Emma', lastName: 'Johnson' },
          { id: '2', firstName: 'Liam', lastName: 'Smith' },
          { id: '3', firstName: 'Sophia', lastName: 'Williams' }
        ]);
      }
    };
    
    loadStudents();
  }, []);
  
  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        // Ensure all expected fields exist
        notes: initialData.notes || ''
      });
    } else {
      resetForm();
    }
  }, [initialData, open]);
  
  useEffect(() => {
    // Recalculate totals whenever items change
    calculateTotals();
  }, [formData.items, formData.tax]);
  
  const resetForm = () => {
    setFormData({
      studentId: '',
      studentName: '',
      issueDate: new Date().toISOString().split('T')[0],
      dueDate: new Date(new Date().setDate(new Date().getDate() + 14)).toISOString().split('T')[0],
      items: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
      subtotal: 0,
      tax: 0,
      total: 0,
      status: 'unpaid',
      paidAmount: 0,
      notes: ''
    });
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // If student is selected, update the student name
    if (name === 'studentId') {
      const selectedStudent = students.find(s => s.id === value);
      if (selectedStudent) {
        setFormData(prev => ({
          ...prev,
          studentId: value,
          studentName: `${selectedStudent.firstName} ${selectedStudent.lastName}`
        }));
      }
    }
  };
  
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index][field] = value;
    
    // Calculate amount for the item
    if (field === 'quantity' || field === 'rate') {
      updatedItems[index].amount = updatedItems[index].quantity * updatedItems[index].rate;
    }
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };
  
  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, rate: 0, amount: 0 }]
    }));
  };
  
  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const updatedItems = [...formData.items];
      updatedItems.splice(index, 1);
      
      setFormData(prev => ({
        ...prev,
        items: updatedItems
      }));
    }
  };
  
  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const taxAmount = (subtotal * (formData.tax / 100)) || 0;
    const total = subtotal + taxAmount;
    
    setFormData(prev => ({
      ...prev,
      subtotal,
      total
    }));
  };
  
  const handleSubmit = () => {
    // Basic validation
    if (!formData.studentId || !formData.issueDate || !formData.dueDate) {
      alert('Please fill in all required fields');
      return;
    }
    
    if (formData.items.some(item => !item.description || item.quantity <= 0)) {
      alert('Please fill in all item details correctly');
      return;
    }
    
    onSubmit({
      ...formData,
      subtotal: parseFloat(formData.subtotal),
      tax: parseFloat(formData.tax),
      total: parseFloat(formData.total),
      paidAmount: parseFloat(formData.paidAmount)
    });
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          {initialData ? 'Edit Invoice' : 'Create New Invoice'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ p: 1 }}>
          <Grid container spacing={3}>
            {/* Invoice Header */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Student</InputLabel>
                <Select
                  name="studentId"
                  value={formData.studentId}
                  label="Student"
                  onChange={handleInputChange}
                >
                  <MenuItem value="">
                    <em>Select a student</em>
                  </MenuItem>
                  {students.map(student => (
                    <MenuItem key={student.id} value={student.id}>
                      {student.firstName} {student.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  label="Status"
                  onChange={handleInputChange}
                >
                  <MenuItem value="unpaid">Unpaid</MenuItem>
                  <MenuItem value="partial">Partially Paid</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Issue Date"
                name="issueDate"
                type="date"
                value={formData.issueDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Due Date"
                name="dueDate"
                type="date"
                value={formData.dueDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            {/* Invoice Items */}
            <Grid item xs={12}>
              <Box sx={{ my: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" color={THEME_COLORS.primary} gutterBottom>
                  Invoice Items
                </Typography>
                
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'rgba(39, 35, 97, 0.05)' }}>
                        <TableCell width="40%">Description</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Rate</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell width="5%"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              value={item.description}
                              onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                              placeholder="Item description"
                              variant="standard"
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                              variant="standard"
                              InputProps={{ inputProps: { min: 1 } }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.rate}
                              onChange={(e) => handleItemChange(index, 'rate', parseFloat(e.target.value) || 0)}
                              variant="standard"
                              InputProps={{ inputProps: { min: 0, step: "any" } }}
                            />
                          </TableCell>
                          <TableCell>
                            {formatCurrency(item.amount || 0)}
                          </TableCell>
                          <TableCell>
                            <IconButton 
                              size="small" 
                              onClick={() => removeItem(index)}
                              disabled={formData.items.length <= 1}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addItem}
                  sx={{
                    mt: 2,
                    borderColor: THEME_COLORS.primary,
                    color: THEME_COLORS.primary,
                    '&:hover': {
                      borderColor: '#1e1c4e',
                      bgcolor: 'rgba(39, 35, 97, 0.04)'
                    }
                  }}
                >
                  Add Item
                </Button>
              </Box>
            </Grid>
            
            {/* Invoice Totals */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                multiline
                rows={4}
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Additional notes for the invoice"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body1">Subtotal:</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatCurrency(formData.subtotal)}
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body1" mr={1}>Tax (%):</Typography>
                    <TextField
                      size="small"
                      type="number"
                      name="tax"
                      value={formData.tax}
                      onChange={handleInputChange}
                      sx={{ width: '70px' }}
                      InputProps={{ inputProps: { min: 0, max: 100, step: "any" } }}
                    />
                  </Box>
                  <Typography variant="body1" fontWeight="medium">
                    {formatCurrency((formData.subtotal * (formData.tax / 100)) || 0)}
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body1" fontWeight="bold">Total:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {formatCurrency(formData.total)}
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1">Amount Paid:</Typography>
                  <TextField
                    size="small"
                    type="number"
                    name="paidAmount"
                    value={formData.paidAmount}
                    onChange={handleInputChange}
                    sx={{ width: '120px' }}
                    InputProps={{ inputProps: { min: 0, step: "any" } }}
                  />
                </Box>
                
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Typography variant="body1" fontWeight="bold">Balance Due:</Typography>
                  <Typography variant="body1" fontWeight="bold" color={formData.total - formData.paidAmount > 0 ? '#f44336' : '#4caf50'}>
                    {formatCurrency(Math.max(0, formData.total - formData.paidAmount))}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          sx={{
            bgcolor: THEME_COLORS.primary,
            '&:hover': {
              bgcolor: '#1e1c4e'
            }
          }}
        >
          {initialData ? 'Update Invoice' : 'Create Invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceForm;